import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui';
import FirstRunSteps from './FirstRunSteps';

export const FIELDS = [
  'Accounting & finance',
  'Consulting & advisory',
  'Marketing agency',
  'E-commerce',
  'Legal',
  'Software',
  'Recruitment',
];

export const FUNCTION_FOR_FIELD = {
  'Accounting & finance': 'Finance & Accounting',
  'Consulting & advisory': 'Operations',
  'Marketing agency': 'Marketing',
  'E-commerce': 'Sales',
  Legal: 'Legal & Compliance',
  Software: 'Product & Engineering',
  Recruitment: 'People & HR',
};

export function fieldForFunction(groupFunction) {
  const match = FIELDS.find((f) => FUNCTION_FOR_FIELD[f] === groupFunction);
  return match || groupFunction;
}

const OTHER = '+ Something else';

export default function FirstRunWork() {
  const navigate = useNavigate();
  const [chip, setChip] = useState(''); // a FIELDS entry, OTHER, or ''
  const [other, setOther] = useState('');
  const [role, setRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    axios
      .get('/user/profile')
      .then((res) => {
        if (!live) return;
        const p = (res && res.data) || {};
        if (p.role) setRole((r) => r || p.role);
        const gf = fieldForFunction((p.group_function || '').trim());
        if (gf) {
          if (FIELDS.includes(gf)) setChip((c) => c || gf);
          else {
            setChip((c) => c || OTHER);
            setOther((o) => o || gf);
          }
        }
      })
      .catch((e) => console.warn('Could not load profile', e));
    return () => {
      live = false;
    };
  }, []);

  const field = chip === OTHER ? other.trim() : chip;
  const canSubmit = !!field && !!role.trim() && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(false);
    try {
      await axios.patch('/user/organisation', { industry: field });
    } catch (err) {
      // 403 is expected for non-admin members; anything else is non-fatal too.
      if (!(err && err.response && err.response.status === 403)) {
        console.warn('Could not save organisation field', err);
      }
    }
    try {
      await axios.patch('/user/profile', { role: role.trim(), group_function: FUNCTION_FOR_FIELD[chip] ?? field });
      navigate('/start/ways');
    } catch (err) {
      console.warn('Could not save profile', err);
      setError(true);
      setSaving(false);
    }
  };

  const skip = () => {
    localStorage.setItem('onboarding_skipped', '1');
    navigate('/app');
  };

  const chipClass = (on, ghost) =>
    `px-3.5 py-2 rounded-xl border-1.5 font-semibold text-[15px] focus-ring ${
      on
        ? 'bg-hl-soft border-ink text-ink'
        : ghost
        ? 'bg-card border-dashed border-pencil text-pencil'
        : 'bg-card border-ink text-ink'
    }`;

  return (
    <main className="max-w-3xl mx-auto px-6 py-10 flex flex-col gap-8">
      <FirstRunSteps current={1} />
      <h1 className="font-display font-extrabold text-5xl leading-none tracking-tight text-ink">
        First, tell us about{' '}
        <span className="bg-hl px-1 rounded-sm">your work</span>.
      </h1>
      <form onSubmit={submit} className="flex flex-col gap-8">
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-bold text-ink">What does your organisation do?</h2>
          <div className="flex flex-wrap gap-2">
            {FIELDS.map((f) => (
              <button
                key={f}
                type="button"
                aria-pressed={chip === f}
                onClick={() => setChip(f)}
                className={chipClass(chip === f)}
              >
                {f}
              </button>
            ))}
            <button
              type="button"
              aria-pressed={chip === OTHER}
              onClick={() => setChip(OTHER)}
              className={chipClass(chip === OTHER, true)}
            >
              {OTHER}
            </button>
          </div>
          {chip === OTHER && (
            <input
              type="text"
              aria-label="Your field"
              value={other}
              maxLength={100}
              onChange={(e) => setOther(e.target.value)}
              className="h-14 w-full bg-card border-1.5 border-ink rounded-[14px] px-4 text-[17px] text-ink focus-ring"
            />
          )}
        </section>
        <section className="flex flex-col gap-3">
          <label htmlFor="first-run-role" className="text-lg font-bold text-ink">
            And what's your role?
          </label>
          <input
            id="first-run-role"
            type="text"
            value={role}
            maxLength={100}
            onChange={(e) => setRole(e.target.value)}
            placeholder="Operations lead — I run delivery for a 12-person strategy team"
            className="h-14 w-full bg-card border-1.5 border-ink rounded-[14px] px-4 text-[17px] text-ink placeholder:text-pencil focus-ring"
          />
        </section>
        {error && (
          <p role="alert" className="text-ink font-semibold">
            Couldn’t save. Try again.
          </p>
        )}
        <div className="flex items-center gap-6">
          <Button type="submit" variant="primary" size="lg" disabled={!canSubmit}>
            {saving ? 'Saving…' : 'Show me what I can do →'}
          </Button>
          <button type="button" onClick={skip} className="text-pencil underline focus-ring">
            Skip for now
          </button>
        </div>
      </form>
    </main>
  );
}
